import employeesApi from 'Api/axios/employees-api';
import { userCourseApi } from 'Api/axios/user-couse-api';
import { userEvaluateCourse } from 'Api/Request';
import { COURSE_STATUS_APPROVED_NAME, S3_EVIDENCES } from 'Constants/General';
import useFetchData from 'Hooks/useFetchData';
import useModal from 'Hooks/useModal';
import useUserCourses from 'Hooks/useUserCourses';
import { useCallback, useEffect, useState } from 'react';
import { s3 } from 'Services/AWS/s3';
import { toast } from 'sonner';
import type { UserDevelopmentPlanI } from 'types';

interface Props {
  userId: string;
}

const useEmployeeDevelopmentPlan = ({ userId }: Props) => {
  // Estado para manejar el Modal de aprobar
  const [selectedCourse, setSelectedCourse] = useState<UserDevelopmentPlanI | null>(null);
  const { open: openApprove, toggle: toggleApprove } = useModal();
  const { open: openResetProgress, toggle: toggleResetProgress } = useModal();
  const { open: openCourseAssign, toggle: toggleCourseAssigner } = useModal();
  const { open: openDeassignConfirmation, toggle: toggleDeassignConfirmation } = useModal();

  const { courses, fetch: fetchAssigned } = useUserCourses({
    init: true,
    userId,
    assigned: true,
    enrolled: true,
  });

  const { data, fetchOne } = useFetchData({
    getOne: id => employeesApi.readDevelopmentPlan(id),
  });

  useEffect(() => {
    fetchOne(userId);
  }, [userId]);

  /**
   * Permite subir el archivo de evaluación al servidor de amazon s3.
   *
   * @param {string} key La llave del archivo en S3.
   * @param {string} file El archivo a subir.
   * @param {string} contentType El tipo de contenido del archivo.
   */
  const uploadEvaluationFileS3 = async (key: string, file: Buffer, contentType: string) => {
    // Si no hay archivo no se hace nada.
    if (!file) return false;

    // Variable para saber el estatus de la transacción.
    let transactionStatus = false;

    try {
      transactionStatus = await s3.uploadFileToBucketWithKeyProvided(key, file, contentType);
    } catch (caught) {
      console.error('Ocurrió un error en "useDevelopmentPlan.uploadEvaluationFileS3": ', caught);
    }

    // Retornamos el estatus de la transacción.
    return !!transactionStatus;
  };

  /**
   * Actualiza la información del curso del empleado
   */
  const updateUserCourse = async (action: 'approve' | 'reset_progress' | 'deassign', file?: any) => {
    switch (action) {
      case 'approve': {
        let key = '';

        // Cuando la prioridad del curso es [Normativa], es necesario subir el archivo de evaluación.
        if (file) {
          key = `${S3_EVIDENCES}/${userId}/${selectedCourse.Course.id}/${file.name}`;

          const uploadStatus = await uploadEvaluationFileS3(key, file, file.type);

          if (uploadStatus === false) {
            return false;
          }
        }

        const response = await userEvaluateCourse({
          id: selectedCourse.userCourseId,
          userCourseStatus: COURSE_STATUS_APPROVED_NAME,
          url: key,
        });

        if (!response.status) {
          // Eliminamos el archivo almacenado en S3 si se subio.
          if (file) {
            await s3.deleteFileFromBucket(key);
          }
          return false;
        }
        break;
      }
      case 'reset_progress': {
        const response = await employeesApi.resetUserCourseProgress(userId, selectedCourse.userCourseId);
        if (response.error) {
          toast.error(response.error);
          return false;
        }

        toggleResetProgress(false);
        toast.success('Progreso reiniciado correctamente');
        break;
      }
      case 'deassign': {
        const response = await userCourseApi.remove(userId, selectedCourse.Course.id);
        if (response.error) {
          toast.error(response.error);
          return false;
        }

        toggleDeassignConfirmation(false);
        toast.success('Curso desasignado correctamente.');
        break;
      }
    }

    fetchOne(userId);
    fetchAssigned(userId);
    return true;
  };

  /**
   * Asigna los cursos seleccionado a un empleado.
   */
  const assignCourses = useCallback(
    async (data: string[]) => {
      const response = await userCourseApi.post(userId, data);
      if (response.error) {
        toast.error(response.error);
        return;
      }

      toast.success(response.data.message);
      fetchAssigned(userId);
    },
    [userId]
  );

  return {
    selectedCourse,
    setSelectedCourse,
    openApprove,
    toggleApprove,
    openResetProgress,
    toggleResetProgress,
    developmentPlan: data,
    updateUserCourse,
    openCourseAssign,
    toggleCourseAssigner,
    assignCourses,
    courses,
    openDeassignConfirmation,
    toggleDeassignConfirmation,
  };
};

export default useEmployeeDevelopmentPlan;
